/**
 * API Service Layer
 * All backend API endpoints organized by feature
 */

import { get, post, patch, del } from './ajax';
import type { CompanyInfo, AnalysisResult } from '@/types';

// ==================== Types ====================

// In-flight request de-dupe (helps avoid React 18 StrictMode dev double-effects triggering
// duplicate network calls with identical payloads).
const inFlightTopicSuggestRequests = new Map<string, Promise<{ success: boolean; data: TopicSuggestion[] }>>();

export interface Citation {
  id: string;
  title?: string;
  url: string;
  domain?: string;
  citation_source?: 'blog' | 'reddit' | 'twitter' | 'linkedin' | 'other';
  snippet?: string;
  citation_count?: number;
  auto_imported: boolean;
  prompt_id?: string;
  reference_source: 'auto' | 'manual';
  created_at?: string;
}

export interface TopicSuggestion {
  id: string;
  title: string;
  description?: string;
  relevance_score: number;
  suggested_keywords?: string[];
}

export interface CitationRef {
  id: string;
  title?: string;
  url: string;
  source?: 'auto' | 'manual';
}

export interface GEOScoreCheck {
  name: string;
  passed: boolean;
  description?: string;
  score: number;
  weight: number;
}

export interface GEOScoreBreakdown {
  category: string;
  score: number;
  status: 'pass' | 'warn' | 'fail';
  checks: GEOScoreCheck[];
}

export interface GEOScoreStatistics {
  pass_count: number;
  warn: number;
  fail: number;
}

export interface GEOScoreData {
  total_score: number;
  grade: string;
  breakdown: GEOScoreBreakdown[];
  statistics: GEOScoreStatistics;
  detected_skills: string[];
}

export interface ContentPlatformResult {
  platform: string;
  content: string;
  markdown: string;
  geo_score: GEOScoreData;
  detected_skills: string[];
  token_usage?: {
    prompt: number;
    completion: number;
  };
}

export type GenerationJobStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface ContentGenerateStatusResponse {
  success: boolean
  generation_id: string
  status: GenerationJobStatus
  progress?: Record<string, string>
  results?: Record<string, Omit<ContentPlatformResult, 'platform'>>
  error_message?: string
  created_at: string
  completed_at?: string
  library_item_id?: string
}

export interface ContentPlatformData {
  content: string;
  markdown_source?: string;
  score: number;
  geo_breakdown?: GEOScoreData;
  published_url?: string;
}

export interface ContentLibraryItem {
  id: string;
  title: string;
  target_query?: string;
  target_queries?: string[];
  prompt_ids?: string[];
  status: 'draft' | 'published' | 'scheduled';
  generation_status?: 'pending' | 'processing' | 'completed' | 'failed';
  platforms: Record<string, ContentPlatformData>;
  citations?: number;
  has_attribution?: boolean;
  traffic?: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  scheduled_publish_at?: string;
}

export interface Prompt {
  id: string;
  text: string;
  category: 'blog' | 'reddit';
  loading: boolean;
  created_at: string;
  updated_at: string;
}

export interface PromptResult {
  id: string;
  prompt_id: string;
  platform_id: 'chatgpt' | 'claude' | 'gemini' | 'perplexity';
  mentioned: boolean;
  sentiment?: 'positive' | 'neutral' | 'negative';
  citation_count: number;
  brand_citation_count: number;
  status?: 'pending' | 'running' | 'done' | 'failed';
  created_at: string;
  updated_at: string;
}

export interface PromptWithResults extends Prompt {
  results: PromptResult[];
}

export interface CitationInDetail {
  id: string;
  url: string;
  title?: string;
  snippet?: string;
  domain?: string;
  brand_cited: boolean;
}

export interface PlatformDetail {
  platform_id: 'chatgpt' | 'claude' | 'gemini' | 'perplexity';
  mentioned: boolean;
  sentiment?: 'positive' | 'neutral' | 'negative';
  citation_count: number;
  raw_answer_text?: string;
  status?: 'pending' | 'running' | 'done' | 'failed';
  citations: CitationInDetail[];
}

export interface PromptDetailResponse {
  success: boolean;
  id: string;
  text: string;
  category: 'blog' | 'reddit';
  loading: boolean;
  platforms: PlatformDetail[];
}

// ==================== Generated Prompts Types ====================

export interface GeneratedPrompt {
  id: string;
  text: string;
  created_at: string;
}

export type GenerateMode = 'fresh' | 'more';

export interface GeneratedPromptListResponse {
  success: boolean;
  data: GeneratedPrompt[];
}

export interface GeneratedPromptDeleteResponse {
  success: boolean;
  message: string;
}

// ==================== Auth Types ====================

export interface User {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
  is_superuser: boolean;
  created_at: string;
  updated_at: string;
  credits?: number;
  company?: {
    id: string;
    name: string;
    url: string;
    domain?: string;
    about?: string;
    industry?: string;
    description?: string;
  } | null;
}

// ==================== Attribution Types ====================

export interface AttributionCredentialExists {
  exists: boolean;
  key_id?: string;
  client_name?: string;
}

export interface AttributionTestResult {
  ok: boolean;
  detail: string;
  key_id?: string;
  client_name?: string;
}

export interface AttributionSaveResponse {
  message: string;
  key_id?: string;
  client_name?: string;
}

export interface TrafficSessionData {
  url: string;
  session_count: number;
}

export interface TrafficSessionsResponse {
  success: boolean;
  data: TrafficSessionData[];
}

// ==================== Cititations API ====================

export const citations = {
  /**
   * Get citations for a specific prompt (auto-import)
   */
  getByPrompt: (promptId: string) =>
    get<{ success: boolean; data: Citation[] }>(`/api/v1/citations/prompt/${promptId}`),

  /**
   * Create a manual citation
   */
  create: (data: {
    title?: string;
    url: string;
    domain?: string;
    snippet?: string;
    prompt_id?: number;
  }) =>
    post<{ success: boolean; data: Citation }>('/api/v1/citations', data),

  /**
   * Delete a citation
   */
  delete: (citationId: string) =>
    del<{ success: boolean; message: string }>(`/api/v1/citations/${citationId}`),
};

// ==================== Topic Suggestions API ====================

export const topics = {
  /**
   * Generate AI topic suggestions
   */
  suggest: (params: {
    prompt_ids?: string[];
    target_prompts?: string[];
    platforms?: string[];
    topic?: string;
    count?: number;
  }) => {
    // React 18 StrictMode (dev) may mount/unmount/remount components which can trigger
    // duplicate effects and thus duplicate requests. De-dupe same-payload in-flight calls
    // to avoid hitting the backend twice.
    const payload = {
      prompt_ids: params.prompt_ids || [],
      target_prompts: params.target_prompts || [],
      platforms: params.platforms || ['blog'],
      topic: params.topic || '',
      count: params.count || 5,
    };

    const key = JSON.stringify(payload);
    const existing = inFlightTopicSuggestRequests.get(key);
    if (existing) return existing;

    const req = post<{ success: boolean; data: TopicSuggestion[] }>(
      '/api/v1/content/suggest_topics',
      payload
    ).finally(() => {
      inFlightTopicSuggestRequests.delete(key);
    });

    inFlightTopicSuggestRequests.set(key, req);
    return req;
  },
};

// ==================== Content Generation API ====================

export const content = {
  /**
   * Generate content for multiple platforms (async)
   * Returns immediately with generation_id. Use getStatus to poll for results.
   */
  generate: (params: {
    topic: string;
    platforms: string[];
    citations: CitationRef[];
    target_prompts?: string[];
    active_skills?: string[];
    context_slots?: Record<string, string>;
    custom_prompt?: string;
  }) =>
    post<{ success: boolean; generation_id: string; message: string; library_item_id?: string }>(
      '/api/v1/content/generate',
      params
    ),

  /**
   * Get status of a generation job
   */
  getStatus: (generationId: string) =>
    get<ContentGenerateStatusResponse>(
      `/api/v1/content/status/${generationId}`
    ),

  /**
   * Regenerate content for a specific platform
   */
  regenerate: (params: {
    platform: string;
    generation_id: string;
    temperature?: number;
    different_creativity?: boolean;
  }) =>
    post<{ success: boolean; data: Record<string, ContentPlatformResult> }>(
      '/api/v1/content/regenerate',
      {
        platform: params.platform,
        generation_id: params.generation_id,
        temperature: params.temperature ?? 0.8,
        different_creativity: params.different_creativity ?? true,
      }
    ),

  /**
   * Analyze content and calculate GEO score
   */
  analyzeScore: (contentStr: string, platform: string, activeSkills?: string[]) =>
    post<{ success: boolean; data: GEOScoreData }>(
      '/api/v1/content/analyze_score',
      { content: contentStr, platform, active_skills: activeSkills }
    ),
};

// ==================== Content Library API ====================

export const library = {
  /**
   * Save content to library
   */
  save: (params: {
    content_id?: string;
    topic: string;
    target_query?: string;
    target_queries?: string[];
    prompt_ids?: string[];
    platform: string;
    content: string;
    geo_score: number;
    rescore?: boolean;
    active_skills?: string[];
    status?: 'draft' | 'published' | 'scheduled';
    scheduled_publish?: string;
  }) =>
    post<{ success: boolean; data: ContentLibraryItem }>('/api/v1/library/save', params),

  /**
   * Get all library items
   */
  getAll: (params?: {
    status?: 'draft' | 'published' | 'scheduled';
    limit?: number;
    offset?: number;
  }) =>
    get<{ success: boolean; data: ContentLibraryItem[]; total: number }>('/api/v1/library', params),

  /**
   * Get a specific library item
   */
  getById: (id: string) =>
    get<{ success: boolean; data: ContentLibraryItem }>(`/api/v1/library/${id}`),

  /**
   * Update library item status
   */
  updateStatus: (
    id: string,
    status: 'draft' | 'published' | 'scheduled'
  ) =>
    patch<{ success: boolean; data: ContentLibraryItem }>(`/api/v1/library/${id}`, { status }),

  /**
   * Update library item fields (status / target prompts metadata)
   */
  update: (
    id: string,
    payload: Partial<{
      status: 'draft' | 'published' | 'scheduled'
      target_query: string
      target_queries: string[]
      prompt_ids: string[]
    }>
  ) =>
    patch<{ success: boolean; data: ContentLibraryItem }>(`/api/v1/library/${id}`, payload),

  /**
   * Update published URL for a specific platform on a library item
   */
  updatePlatformPublishedUrl: (id: string, platform: string, published_url: string | null) =>
    patch<{ success: boolean; data: ContentLibraryItem }>(
      `/api/v1/library/${id}/platform/${platform}`,
      { published_url }
    ),

  /**
   * Delete a library item
   */
  delete: (id: string) =>
    del<{ success: boolean; message: string }>(`/api/v1/library/${id}`),
};

// ==================== Prompt API ====================

export const prompts = {
  /**
   * List prompts with results
   */
  getAll: (params?: { limit?: number; offset?: number }) =>
    get<{ success: boolean; data: PromptWithResults[] }>('/api/v1/prompts', params),

  /**
   * Create a new prompt (automatically enqueues tracking)
   */
  create: (data: { text: string; category?: 'blog' | 'reddit' }) =>
    post<{ success: boolean; data: Prompt }>('/api/v1/prompts', {
      text: data.text,
      category: data.category ?? 'blog',
    }),

  /**
   * Delete a prompt
   */
  delete: (promptId: string) =>
    del<{ success: boolean; message: string }>(`/api/v1/prompts/${promptId}`),

  /**
   * Manually trigger tracking for a prompt
   */
  track: (promptId: string) =>
    post<{ success: boolean; message: string; prompt_id: string }>(
      `/api/v1/prompts/${promptId}/track`,
      {}
    ),

  /**
   * Get detailed tracking results for a prompt
   */
  getDetail: (promptId: string) =>
    get<PromptDetailResponse>(`/api/v1/prompts/${promptId}/detail`),

  // ==================== Generated Prompts ====================

  /**
   * Get cached generated prompts for the current user
   */
  getGenerated: () =>
    get<GeneratedPromptListResponse>('/api/v1/prompts/generated'),

  /**
   * Generate AI prompts based on campaign info
   * - mode="fresh": Clear cache and generate new prompts
   * - mode="more": Append to existing prompts (with deduplication)
   */
  generatePrompts: (params: { count?: number; mode?: GenerateMode }) =>
    post<GeneratedPromptListResponse>('/api/v1/prompts/generated', {
      count: params.count ?? 5,
      mode: params.mode ?? 'fresh',
    }),

  /**
   * Delete a specific generated prompt from cache
   */
  deleteGenerated: (generatedId: string) =>
    del<GeneratedPromptDeleteResponse>(`/api/v1/prompts/generated/${generatedId}`),
};

// ==================== Auth API ====================

export const auth = {
  /**
   * Sync user info from Logto /me to backend DB.
   * Call after sign-in callback to persist email/name.
   */
  syncUserinfo: async () => {
    const { getUserinfoAccessToken } = await import('./tokenBridge')
    const token = await getUserinfoAccessToken()
    if (!token) return
    const { post } = await import('./ajax')
    await post<any>('/api/v1/auth/sync_userinfo', {}, {
      headers: { 'X-Userinfo-Token': `Bearer ${token}` },
      skipAuth: true,
    })
  },

  /**
   * Get current user info (works with both Logto and dev mode)
   */
  getMe: async () => {
    // Backend returns UserResponse directly (no {success,data} wrapper)
    const res = await get<any>('/api/v1/auth/me')
    if (res && typeof res === 'object' && 'success' in res) return res as { success: boolean; data: User }
    return { success: true, data: res as User }
  },

  /**
   * Logout user (for logging purposes, actual logout handled by Logto)
   */
  logout: async () => {
    try {
      const res = await post<any>('/api/v1/auth/logout', {})
      if (res && typeof res === 'object' && 'success' in res) return res as { success: boolean; message: string }
      return { success: true, message: (res?.message as string) ?? 'Successfully logged out' }
    } catch {
      // Ignore logout errors
      return { success: true, message: 'Logged out' }
    }
  },

  /**
   * Check if current user has admin permission
   */
  checkAdmin: async () => {
    const res = await get<{ is_admin: boolean; user_id: string; scopes: string[] }>('/api/v1/auth/check_admin')
    return res
  },
};

// ==================== Attribution API ====================

export const attribution = {
  /**
   * 检查用户的 Attribution 凭证是否存在
   */
  getCredentialExists: () =>
    get<AttributionCredentialExists>('/api/v1/attribution/credential_exists'),

  /**
   * 测试 Attribution API Key 连接（保存前验证）
   */
  testConnection: (apiKey: string) =>
    post<AttributionTestResult>('/api/v1/attribution/test_connection', { api_key: apiKey }),

  /**
   * 保存或更新用户的 Attribution API Key 凭证
   */
  saveCredential: (apiKey: string) =>
    post<AttributionSaveResponse>('/api/v1/attribution/save_credential', { api_key: apiKey }),

  /**
   * 删除用户的 Attribution API Key 凭证
   */
  deleteCredential: () =>
    get<{ message: string }>('/api/v1/attribution/delete_credential'),

  /**
   * 批量查询多个 URL 的会话数量
   */
  getTrafficSessionsByUrls: (urls: string[]) =>
    post<TrafficSessionsResponse>('/api/v1/attribution/traffic/sessions_by_urls', { urls }),
};

// ==================== Company API ====================

export const company = {
  /**
   * Create company and run full analysis
   */
  analyze: (data: { url: string }) =>
    post<{ success: boolean; data: CompanyInfo }>('/api/v1/company/analyze', data),

  /**
   * Get user's active company with analysis result
   */
  getMyInfo: () =>
    get<{ success: boolean; data: { company: CompanyInfo | null; analysisResult: AnalysisResult | null } }>(
      '/api/v1/company/my_info'
    ),

  /**
   * Update company info
   */
  update: (data: { name?: string; industry?: string; description?: string }) =>
    post<{ success: boolean; data: CompanyInfo }>('/api/v1/company/update', data),

};

// ==================== Skills Library API ====================

export interface SkillCategoryData {
  id: string;
  name: string;
  icon: string | null;
  sort_order: number | null;
  is_system_default: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkillItemData {
  id: string;
  category_id: string;
  name: string;
  summary: string | null;
  content_md: string;
  langfuse_prompt_name: string;
  sort_order: number | null;
  is_system_default: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkillItemWithCategory extends SkillItemData {
  category_name: string;
  category_icon: string | null;
}

export const skills = {
  // -------- Categories --------
  listCategories: (params?: { include_deleted?: boolean }) =>
    get<{ success: boolean; data: SkillCategoryData[] }>('/api/v1/skills/categories', params),

  createCategory: (data: { name: string; icon?: string | null; sort_order?: number }) =>
    post<{ success: boolean; data: SkillCategoryData }>('/api/v1/skills/categories', data),

  updateCategory: (id: string, data: { name?: string; icon?: string | null; sort_order?: number }) =>
    patch<{ success: boolean; data: SkillCategoryData }>(`/api/v1/skills/categories/${id}`, data),

  deleteCategory: (id: string) =>
    del<{ success: boolean; message: string }>(`/api/v1/skills/categories/${id}`),

  restoreCategory: (id: string) =>
    post<{ success: boolean; message: string }>(`/api/v1/skills/categories/${id}/restore`, {}),

  // -------- Items --------
  listItems: (params?: { category_id?: string; include_deleted?: boolean }) =>
    get<{ success: boolean; data: SkillItemWithCategory[] }>('/api/v1/skills/items', params),

  createItem: (data: { category_id: string; name: string; content_md: string; sort_order?: number }) =>
    post<{ success: boolean; data: SkillItemData }>('/api/v1/skills/items', data),

  updateItem: (id: string, data: { category_id?: string; name?: string; content_md?: string; sort_order?: number }) =>
    patch<{ success: boolean; data: SkillItemData }>(`/api/v1/skills/items/${id}`, data),

  deleteItem: (id: string) =>
    del<{ success: boolean; message: string }>(`/api/v1/skills/items/${id}`),

  restoreItem: (id: string) =>
    post<{ success: boolean; message: string }>(`/api/v1/skills/items/${id}/restore`, {}),

  // -------- System Skills (read-only) --------
  listSystemCategories: () =>
    get<{ success: boolean; data: SkillCategoryData[] }>('/api/v1/skills/system_categories'),

  listSystemItems: (params?: { category_id?: string }) =>
    get<{ success: boolean; data: SkillItemWithCategory[] }>('/api/v1/skills/system_items', params),
};

// ==================== Credits API ====================

export interface CreditBalance {
  credits_balance: number;
  usd_equivalent: number;
}

export interface CreditSummary {
  current_balance: number;
  current_balance_usd: number;
  period_days: number;
  period_total_credits: number;
  period_total_usd: number;
  period_total_tokens: number;
  period_record_count: number;
  daily_avg_credits: number;
  daily_avg_usd: number;
}

export const credits = {
  getBalance: () =>
    get<CreditBalance>('/api/v1/credits/balance'),

  getSummary: (params?: { days?: number }) =>
    get<CreditSummary>('/api/v1/credits/summary', params),
};

// ==================== Payment API ====================

export const payment = {
  /**
   * Create a Stripe Checkout session for a plan/pack purchase
   */
  createSession: (planId: string) =>
    post<{ success: boolean; session_id: string; checkout_url: string }>(
      '/api/v1/payment/create_session',
      { plan_id: planId }
    ),

  /**
   * Check payment status after Stripe redirect
   */
  checkSession: (sessionId: string) =>
    get<{ success: boolean; status: string; credits_added: string }>(
      '/api/v1/payment/check_session',
      { session_id: sessionId }
    ),
};

// ==================== Credit Estimate API ====================

export const creditEstimate = {
  /**
   * Estimate credits for a given scenario
   */
  estimate: (data: {
    scenario: string
    params: Record<string, any>
    required_fraction?: number
  }) =>
    post<{
      scenario: string
      estimated_min_credits: number
      estimated_max_credits: number
      required_fraction: number
      required_minimum_balance: number
      breakdown?: Record<string, any>
    }>('/api/v1/credit_estimate/estimate', data),

  /**
   * Check if user has sufficient balance for a scenario
   */
  checkBalance: (data: { scenario: string; params: Record<string, any> }) =>
    post<{
      sufficient: boolean
      current_balance: number
      required_balance: number
      shortfall: number
    }>('/api/v1/credit_estimate/check_balance', data),
};

// Export all APIs as a single object
export const api = {
  citations,
  topics,
  content,
  library,
  prompts,
  auth,
  attribution,
  company,
  skills,
  credits,
  payment,
  creditEstimate,
};

export default api;
